import { Task, TaskType, Timestamp } from '@griply/types';

export type DateWindow = {
  start?: Timestamp;
  end?: Timestamp;
};

// The shared active/completed task subscriptions return the whole task
// collection — Habit and Repeating template docs live there too. Only the
// server's `tasksOnly` constraint keeps them out of the task bucket, so any
// client-side consumer that counts tasks must partition on type first.
export const isTaskTypeTask = (task: Task): boolean =>
  task.type === TaskType.Task;

export const isUncompleted = (task: Task): boolean => !task.completedAt;

export const isCompleted = (task: Task): boolean => !!task.completedAt;

export const isNotFrozen = (task: Task): boolean => !task.frozenAt;

export const isNotArchived = (task: Task): boolean => !task.archivedAt;

export const isArchived = (task: Task): boolean => !!task.archivedAt;

export const hasNoSchedules = (task: Task): boolean => !task.schedules?.length;

// Timeslot pseudo-tasks (goal/life-area calendar slots) are not user to-dos and
// must stay out of every task list.
export const hasNoAction = (task: Task): boolean => !task.action;

// Inclusive on both bounds, mirroring the server's `>=` start / `<=` end.
const isWithinWindow = (
  date: Timestamp | null | undefined,
  { start, end }: DateWindow,
): boolean => {
  if (!date) {
    return false;
  }
  const time = date.getTime();
  if (start && time < start.getTime()) {
    return false;
  }
  if (end && time > end.getTime()) {
    return false;
  }
  return true;
};

export const isWithinDeadlineWindow = (
  task: Task,
  window: DateWindow,
): boolean => isWithinWindow(task.endStrategy?.deadline, window);

export const isWithinDeadlineDeadlineWindow = (
  task: Task,
  window: DateWindow,
): boolean => isWithinWindow(task.deadlineDeadline, window);

export type ActiveInWindowOptions = {
  // Repeating/habit templates carry schedules and are counted by their own
  // pipelines, so deadline-window surfaces that render plain to-dos exclude them.
  requireNoSchedules?: boolean;
  // Match on `deadlineDeadline` instead of the scheduled deadline.
  useDeadlineDeadline?: boolean;
};

// The predicate every deadline-window feeder shares. The *window* each surface
// wants is per-surface (this month / rest of month / next ten days / overdue),
// so each hook still computes its own — only the filtering is shared.
export const isActiveInWindow = (
  task: Task,
  window: DateWindow,
  { requireNoSchedules, useDeadlineDeadline }: ActiveInWindowOptions = {},
): boolean => {
  const inWindow = useDeadlineDeadline
    ? isWithinDeadlineDeadlineWindow(task, window)
    : isWithinDeadlineWindow(task, window);

  return (
    inWindow &&
    isNotFrozen(task) &&
    isNotArchived(task) &&
    (!requireNoSchedules || hasNoSchedules(task))
  );
};
